import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { fetchComparableCandidates, type ComparableCandidate } from "@/api/discover";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  artistId: string;
  onAdd: (candidate: ComparableCandidate) => Promise<void>;
};

const SEARCH_DELAY_MS = 250;

export default function AddComparableDialog({ open, onOpenChange, artistId, onAdd }: Props) {
  const [candidates, setCandidates] = useState<ComparableCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [addingKey, setAddingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Reads stored artists from the database only; no provider calls, no collection.
    const timer = setTimeout(() => {
      fetchComparableCandidates(artistId, query)
        .then((payload) => {
          if (!cancelled) setCandidates(payload.candidates ?? []);
        })
        .catch(() => {
          if (!cancelled) setError("We couldn't load artists. Please try again.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, query ? SEARCH_DELAY_MS : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, artistId, query]);

  const handleAdd = async (candidate: ComparableCandidate) => {
    setAddingKey(`${candidate.kind}-${candidate.id}`);
    try {
      await onAdd(candidate);
    } finally {
      setAddingKey(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a comparable artist</DialogTitle>
          <DialogDescription>
            Choose artists whose touring history is relevant to where you want to play.
          </DialogDescription>
        </DialogHeader>

        <Input
          placeholder="Search artists by name"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search artists"
        />

        <div className="max-h-80 space-y-2 overflow-y-auto" role="list" aria-label="Artists you can add">
          {loading && candidates.length === 0 && <p className="text-sm text-muted-foreground">Loading artists…</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {!loading && !error && candidates.length === 0 && (
            <div className="rounded-lg border border-border bg-background/40 p-4 text-sm text-muted-foreground">
              {query.trim() ? "No matching artists." : "No artists available yet."}
            </div>
          )}
          {candidates.map((candidate) => {
            const key = `${candidate.kind}-${candidate.id}`;
            return (
              <div
                key={key}
                role="listitem"
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/40 p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">{candidate.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {candidate.genre || "Genre unknown"}
                    {candidate.has_venue_history && (
                      <span className="text-emerald-600 dark:text-emerald-300"> · Venue history available</span>
                    )}
                  </p>
                </div>
                {candidate.already_selected ? (
                  <span className="shrink-0 text-xs font-medium text-muted-foreground" aria-label={`${candidate.name} already added`}>
                    Added
                  </span>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => void handleAdd(candidate)}
                    disabled={addingKey !== null}
                    aria-label={`Add ${candidate.name}`}
                  >
                    {addingKey === key ? "Adding…" : "Add"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground">
          Don't see the right artist?{" "}
          <Link to="/artists" className="font-medium text-primary hover:underline">
            Find more artists
          </Link>
        </p>
      </DialogContent>
    </Dialog>
  );
}
